import * as React from "react";
import { Button } from "../../../components/ui/button";
import { Spinner } from "../../../components/ui/feedback";
import { Input } from "../../../components/ui/input";
import { SearchIcon } from "../../../components/icons";
import { FieldLabel } from "../../../components/forms/FormSection";
import { describeError } from "../../../utils/describeError";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { ProjectSummary, useSearchProjectsInfiniteQuery } from "../api/filingApi";
import ProjectOption from "./ProjectOption";

// Every project the user is a member of, searched on the server and paged as the list
// scrolls. Shared by Outlook's File and the document hosts' Save to Jylo, which choose a
// project the same way.
//
// The selection is a whole project, not an id: a search can scroll the chosen one out
// of the loaded pages, and the sheet still needs its name to say where it went.

const SEARCH_DEBOUNCE_MS = 250;

export interface ProjectSearchListProps {
  /** Ids the inputs are labelled by, unique per sheet. */
  id: string;
  label: string;
  selected: Pick<ProjectSummary, "objectIdentifier"> | null;
  onSelect: (project: ProjectSummary) => void;
  disabled?: boolean;
}

const ProjectSearchList = ({ id, label, selected, onSelect, disabled }: ProjectSearchListProps) => {
  const [query, setQuery] = React.useState("");
  const searchText = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);
  const search = useSearchProjectsInfiniteQuery({ searchText });
  const projects = React.useMemo(
    () => search.data?.pages.flatMap((page) => page.items) ?? [],
    [search.data]
  );

  // Load the next page when the end of the list scrolls into view. A sentinel rather
  // than a scroll handler, so it also fires when the first page is too short to scroll.
  const listRef = React.useRef<HTMLDivElement>(null);
  const endRef = React.useRef<HTMLDivElement>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = search;
  React.useEffect(() => {
    const end = endRef.current;
    if (!end || !hasNextPage) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting) && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { root: listRef.current, rootMargin: "40px" }
    );
    observer.observe(end);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, projects.length]);

  // A new search starts at its own top, not wherever the last one was scrolled to.
  React.useEffect(() => {
    listRef.current?.scrollTo?.({ top: 0 });
  }, [searchText]);

  const searching = search.isFetching && !isFetchingNextPage;

  return (
    <div className="flex min-h-0 flex-col gap-1.5">
      <FieldLabel htmlFor={`${id}-search`}>{label}</FieldLabel>
      <div className="relative">
        <SearchIcon className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          id={`${id}-search`}
          className="pr-8 pl-8"
          placeholder="Search your projects"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          disabled={disabled}
        />
        {searching && <Spinner size={14} className="absolute top-1/2 right-2.5 -translate-y-1/2" />}
      </div>
      <div
        ref={listRef}
        className="flex max-h-[220px] flex-col gap-1.5 overflow-y-auto pr-0.5"
        role="radiogroup"
        aria-label={label}
        aria-busy={search.isFetching}
      >
        {projects.map((project) => (
          <ProjectOption
            key={project.objectIdentifier}
            name={project.name}
            detail={project.description}
            selected={selected?.objectIdentifier === project.objectIdentifier}
            onSelect={() => onSelect(project)}
            disabled={disabled}
          />
        ))}

        {search.isError && (
          <div className="flex flex-col items-start gap-1 py-2">
            <p className="text-body2 text-muted-foreground">
              Your projects could not be loaded. {describeError(search.error)}
            </p>
            <Button variant="outline" onClick={() => void search.refetch()}>
              Try again
            </Button>
          </div>
        )}

        {search.isSuccess && projects.length === 0 && (
          <p className="py-2 text-body2 text-muted-foreground">
            {searchText
              ? `No project of yours matches “${searchText}”.`
              : "You are not a member of any Jylo projects yet."}
          </p>
        )}

        {search.isLoading && (
          <div className="flex justify-center py-2">
            <Spinner size={18} />
          </div>
        )}

        <div ref={endRef} className="flex flex-none justify-center">
          {isFetchingNextPage && <Spinner size={16} className="my-1" />}
        </div>
      </div>
    </div>
  );
};

export default ProjectSearchList;
